import { Landmark, FileText, Banknote } from "lucide-react";
import { useReveal } from "@/lib/useReveal";

const COURTHOUSE =
  "https://images.pexels.com/photos/37493459/pexels-photo-37493459.jpeg";

export const WhatIsSurplus = () => {
  const ref = useReveal();
  return (
    <section
      id="education"
      data-testid="education-section"
      ref={ref}
      className="bg-[#FDFBF7] py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {/* Left: image + caption */}
          <div className="lg:col-span-5 reveal">
            <div className="relative">
              <img
                src={COURTHOUSE}
                alt="County courthouse where surplus funds are held"
                className="w-full h-[520px] object-cover rounded-sm"
              />
              <div className="absolute -bottom-6 -right-6 bg-[#0A192F] text-[#FDFBF7] p-6 max-w-[260px] rounded-sm">
                <div className="text-[#D4AF37] text-xs uppercase tracking-[0.2em] mb-2">
                  Did you know?
                </div>
                <p className="font-serif-display text-xl leading-snug">
                  Billions in surplus funds sit unclaimed in U.S. county
                  treasuries.
                </p>
              </div>
            </div>
          </div>

          {/* Right: explanation */}
          <div className="lg:col-span-7 reveal" style={{ transitionDelay: "120ms" }}>
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#D4AF37]" />
              <span className="text-[#1E3A5F] text-xs uppercase tracking-[0.3em]">
                The Basics
              </span>
            </div>

            <h2 className="font-serif-display text-[#0A192F] text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05] tracking-tight">
              What are
              <br />
              <em className="italic text-[#1E3A5F]">excess proceeds?</em>
            </h2>

            <p className="mt-8 text-[#1E3A5F] text-lg leading-relaxed max-w-2xl">
              When a property is sold at a tax sale or foreclosure auction, it
              often sells for <strong>more</strong> than the debt owed. The
              difference — the <strong>surplus</strong> — is held by the county
              or court. Most former owners never know it exists. State law
              gives <em>you</em> the right to claim it.
            </p>

            <div className="mt-12 space-y-1">
              <Row
                icon={Landmark}
                title="Tax Sale Overages"
                body="Property auctioned over unpaid taxes. Sale price exceeds taxes + fees. The excess belongs to the previous owner — not the government."
              />
              <Row
                icon={FileText}
                title="Mortgage Foreclosure Surplus"
                body="When a foreclosed home sells for more than the mortgage payoff, the surplus is owed to the original homeowner or their heirs."
              />
              <Row
                icon={Banknote}
                title="Estate & Heir Recovery"
                body="Funds belonging to a deceased relative? Heirs can lawfully claim what is left behind, even years after the sale."
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Row = ({ icon: Icon, title, body }) => (
  <div className="flex gap-5 py-6 border-b border-[#E2DCD0] last:border-b-0">
    <div className="shrink-0 h-12 w-12 border border-[#D4AF37]/40 rounded-sm flex items-center justify-center bg-[#F3F0E6]">
      <Icon className="h-5 w-5 text-[#0A192F]" />
    </div>
    <div>
      <h3 className="font-serif-display text-2xl text-[#0A192F] mb-2">
        {title}
      </h3>
      <p className="text-[#1E3A5F] leading-relaxed">{body}</p>
    </div>
  </div>
);
